import axios from 'axios';
import type { Cell } from '../game/types';

// Get API base URL from environment or use proxy/default
const getApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3002';
  }
  const url = process.env.REACT_APP_API_URL || 'https://api.migoyugo.com';
  return url;
};

const API_URL = process.env.NODE_ENV === 'development' ? '' : getApiUrl();

// Helper function to get auth token from localStorage (user token or admin token)
const getAuthToken = (): string | null => {
  // Try admin token first (for admin dashboard), then user token
  return localStorage.getItem('adminToken') || localStorage.getItem('authToken');
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * One move of a puzzle's solution.
 *
 * `alternates` lists other squares that are equally correct at this point. It is
 * only set on the final move, where an Igo can sometimes be completed at either
 * end of an open line of yugos, and every listed square wins outright.
 */
export interface SolutionMove {
  row: number;
  col: number;
  player: 'white' | 'black';
  alternates?: Array<{ row: number; col: number }>;
}

// Puzzle types
export interface Puzzle {
  id: number;
  creator_id: number;
  creator_username: string;
  title: string;
  description: string | null;
  difficulty: 'easy' | 'medium' | 'hard' | 'expert' | 'insane';
  initial_board: (Cell | null)[][];
  starting_player: 'white' | 'black';
  solution_moves: SolutionMove[] | null;
  created_at: string;
  is_public: boolean;
  times_solved: number;
  average_solve_time: number | null;
  status?: 'draft' | 'pending_review' | 'approved' | 'rejected';
  review_message?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  /** Stable public catalog # (approved puzzles only). */
  catalog_number?: number | null;
}

function capitalizeDifficulty(difficulty: string): string {
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
}

function extractIgoInLabel(title: string): string | null {
  const match = String(title || '').match(/Igo in\s+(\d+)/i);
  return match ? `Igo in ${match[1]}` : null;
}

/** Stored title: "White to move - Igo in 4" (no catalog #, no strength). */
export function canonicalPuzzleTitle(
  puzzle: Pick<Puzzle, 'title' | 'starting_player'>
): string {
  const who = puzzle.starting_player === 'black' ? 'Black' : 'White';
  const igo = extractIgoInLabel(puzzle.title || '');
  return igo ? `${who} to move - ${igo}` : `${who} to move`;
}

/** e.g. "#43 - White to move - Igo in 4 - Medium" */
export function formatPuzzleDisplayTitle(
  puzzle: Pick<Puzzle, 'title' | 'difficulty' | 'catalog_number' | 'starting_player'>
): string {
  const difficultyLabel = capitalizeDifficulty(puzzle.difficulty);
  const body = canonicalPuzzleTitle(puzzle);
  if (puzzle.catalog_number != null) {
    return `#${puzzle.catalog_number} - ${body} - ${difficultyLabel}`;
  }
  return `${body} - ${difficultyLabel}`;
}

export interface PuzzleFilters {
  difficulty?: 'easy' | 'medium' | 'hard' | 'expert' | 'insane';
  creator_id?: number;
  is_public?: boolean;
  include_private?: boolean;
  my_puzzles?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: 'created_at' | 'times_solved' | 'title' | 'difficulty' | 'catalog_number';
  orderDir?: 'asc' | 'desc';
}

export interface CreatePuzzleData {
  title: string;
  description?: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'expert' | 'insane';
  initialBoard: (Cell | null)[][];
  startingPlayer: 'white' | 'black';
  solutionMoves?: SolutionMove[];
  isPublic?: boolean;
  status?: 'draft' | 'pending_review' | 'approved' | 'rejected';
}

export interface PuzzlesResponse {
  puzzles: Puzzle[];
  count: number;
  filters: {
    difficulty: string | null;
    creator_id: number | null;
    is_public: boolean | null;
    limit: number | null;
    offset: number | null;
    orderBy: string;
    orderDir: string;
  };
}

export class PuzzleService {
  /**
   * Create a new puzzle
   */
  static async createPuzzle(data: CreatePuzzleData) {
    try {
      const response = await apiClient.post('/api/puzzles', data);
      return {
        success: true,
        error: null,
        puzzle: response.data.puzzle,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to create puzzle',
      };
    }
  }

  /**
   * Get a puzzle by ID
   */
  static async getPuzzleById(id: number) {
    try {
      const response = await apiClient.get(`/api/puzzles/${id}`);
      return {
        success: true,
        error: null,
        puzzle: response.data.puzzle as Puzzle,
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          error: 'Puzzle not found',
        };
      }
      if (error.response?.status === 403) {
        return {
          success: false,
          error: 'Puzzle is private',
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get puzzle',
      };
    }
  }

  /**
   * Get list of puzzles with optional filters
   */
  static async getPuzzles(filters: PuzzleFilters = {}) {
    try {
      // Build query string from filters
      const params = new URLSearchParams();
      
      if (filters.difficulty) {
        params.append('difficulty', filters.difficulty);
      }
      if (filters.creator_id) {
        params.append('creator_id', filters.creator_id.toString());
      }
      if (filters.is_public !== undefined) {
        params.append('is_public', filters.is_public.toString());
      }
      if (filters.include_private) {
        params.append('include_private', 'true');
      }
      if (filters.my_puzzles) {
        params.append('my_puzzles', 'true');
      }
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters.offset) {
        params.append('offset', filters.offset.toString());
      }
      if (filters.orderBy) {
        params.append('orderBy', filters.orderBy);
      }
      if (filters.orderDir) {
        params.append('orderDir', filters.orderDir);
      }

      const queryString = params.toString();
      const url = queryString ? `/api/puzzles?${queryString}` : '/api/puzzles';
      
      const response = await apiClient.get(url);
      return {
        success: true,
        error: null,
        data: response.data as PuzzlesResponse,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get puzzles',
        data: null,
      };
    }
  }

  /**
   * Update an existing puzzle
   */
  static async updatePuzzle(id: number, data: Partial<CreatePuzzleData>) {
    try {
      const response = await apiClient.put(`/api/puzzles/${id}`, data);
      return {
        success: true,
        error: null,
        puzzle: response.data.puzzle,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update puzzle',
      };
    }
  }

  /**
   * Delete a puzzle
   */
  static async deletePuzzle(id: number) {
    try {
      await apiClient.delete(`/api/puzzles/${id}`);
      return {
        success: true,
        error: null,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete puzzle',
      };
    }
  }

  static async getMyPuzzles(): Promise<{ success: boolean; error: string | null; puzzles: Puzzle[] }> {
    try {
      const response = await apiClient.get('/api/puzzles/mine');
      return { success: true, error: null, puzzles: response.data.puzzles || [] };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get your puzzles',
        puzzles: [],
      };
    }
  }

  static async submitPuzzleForReview(id: number): Promise<{ success: boolean; error: string | null }> {
    try {
      await apiClient.post(`/api/puzzles/${id}/submit`);
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to submit puzzle',
      };
    }
  }
}


